import { Component, OnDestroy, OnInit } from '@angular/core';
import { IOrder, IOrderFilterRequest, IOrdersFilter } from '../../models/order.model';
import { AppState } from 'src/app/core/app.state';
import { Actions, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { GetOrders, RefreshOrders } from '../../store/orders.actions';
import * as quotationsActions from 'src/app/features/quotations/store/quotations.actions';
import { AtoProcessingStatus } from 'src/app/features/quotations/enum/quotations.status.enum';
import { combineLatest, debounceTime, distinctUntilChanged, map, Observable, Subject, Subscription, takeUntil, timer } from 'rxjs';
import { DATE_TIME_FORMAT, DEFAULT_PAGE_SIZE, PAGE_SIZE_OPTIONS } from 'src/app/config/global.config';
import { TranslateService } from '@ngx-translate/core';
import { encodeFilterObject, formatResponseTime, parseFilterRequest, parseOrdersFilterRequest, refillFilter } from 'src/app/shared/utils/helpers';
import { userInitials } from 'src/app/shared/utils/order-users-dedupe';
import { IPageCount, IPagination } from 'src/app/shared/models/page.model';
import { IOrderKPI } from '../../models/order.model';
import { ActivatedRoute, Router } from '@angular/router';
import { resolveAttentionCount, resolveLineCount } from '../../../quotations/utils/line-composition.util';

@Component({
  selector: 'app-list-orders',
  templateUrl: './list-orders.component.html',
  styleUrls: ['./list-orders.component.scss']
})
export class ListOrdersComponent implements OnInit, OnDestroy {

  /**
   * Order-lines figure (WB-8066). Replaces the stacked "Total SKU / Total
   * quantity" pair this cell used to show — both numbers now live in the
   * breakdown popover, shared with the orders list and the order detail page.
   */
  orderLinesCount(order: IOrder): number {
    return resolveLineCount(order.line_composition, order);
  }

  /** Amber badge: needs review + unmatched. 0 hides the badge. */
  attentionCount(order: IOrder): number {
    return resolveAttentionCount(order.line_composition);
  }
  page: IPageCount = {
    size: DEFAULT_PAGE_SIZE,
    number: 1,
  }
  pagination$: Observable<IPagination | null> = this.store.select((state) => state.orders.metadata);
  DATE_FORMAT = DATE_TIME_FORMAT;
  orders$: Observable<IOrder[]> = this.store.select(state => state.orders.orders);
  showProcessing$: Observable<boolean> = this.store.select(state => state.orders.showProcessing);
  displayOrders$: Observable<IOrder[]> = combineLatest([
    this.orders$,
    this.showProcessing$,
  ]).pipe(
    map(([orders, showProcessing]) => {
      const placeholderRows = orders.filter((order) => this.isPlaceholder(order));
      const normalRows = orders.filter((order) => !this.isPlaceholder(order));
      return showProcessing ? [...placeholderRows, ...normalRows] : normalRows;
    })
  );
  filter: IOrderFilterRequest | undefined = undefined;
  key: string | undefined = undefined;
  kpi$: Observable<IOrderKPI | undefined> = this.store.select((state)=> state.orders.kpi);
  subscriptions: Subscription[] = []
  isAppLoading$ = this.store.select((state) => state.shared.isAppLoading);

  now = Date.now();
  private destroy$ = new Subject<void>();

  activeSortBy: string  = 'id';
  activeSortMode: string = 'desc';

  unparsedFilter:IOrdersFilter | null = null;
  module:string = 'order';

  formatResponseTime = formatResponseTime
  userInitials = userInitials;

  public getStatusTranslation(status: string): string {
    return this.translateService.instant(`quotations_and_orders.OrderShippingStatus.${status}`) || status;
  }
  constructor(
    private store: Store<AppState>,
    private translateService: TranslateService,
    private router: Router,
    private activatedRoute: ActivatedRoute,
    private actions$: Actions
  ) {
    this.activatedRoute.queryParams.subscribe(params => {
      const pageNumber = parseInt(params['page'] || '1');
      const pageCount = PAGE_SIZE_OPTIONS.includes(parseInt(params['count'])) ? parseInt(params['count']) : DEFAULT_PAGE_SIZE;
      this.key = params['keyword'] ?? '';
      if(params['filter'])
      {
        try {
          this.unparsedFilter = refillFilter(this.module, JSON.parse(decodeURIComponent(params['filter']))) as IOrdersFilter
          if(this.unparsedFilter)
            this.filter = parseFilterRequest(this.module, this.unparsedFilter);
        } catch (error) {
          this.filter == undefined
        }
      }
      this.page = { ...this.page, number: pageNumber, size: pageCount };
    });
   }

  ngOnInit(): void {
    const sub1 = this.store
      .select((state) => state.orders.ordersFilterState)
      .pipe(
        debounceTime(10),
        distinctUntilChanged((prev, curr) => JSON.stringify(prev) === JSON.stringify(curr))
      )
      .subscribe((filter) => {
        this.unparsedFilter = filter.advanced;
        this.filter = parseFilterRequest(this.module, filter.advanced);
        this.key = filter.keyword;
        this.toPage(this.page);
      });

    this.subscriptions.push(sub1);

    this.actions$
      .pipe(ofType(RefreshOrders), takeUntil(this.destroy$))
      .subscribe(() => this.toPage(this.page));

    timer(0, 1000)
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => (this.now = Date.now()));
  }

  toPage(page: IPageCount) {
    this.page = { ...this.page, number: page.number, size: page.size };
    this.store.dispatch(
      GetOrders({ 
        page: this.page, 
        filter: this.filter, 
        key: this.key, 
        sort_by: this.activeSortBy, 
        sort_mode: this.activeSortMode 
      })
    );
    let filter:any = {}
    if(this.key && this.key != ''){
      filter.keyword = this.key;
    }
    filter.filter = encodeFilterObject(this.unparsedFilter);
    let queryParams = { page: this.page.number, count: this.page.size, ...filter };
    this.router.navigate([], {
      queryParams: queryParams
    });
  }

  getStatusStyles(order_status: string): { [key: string]: string } {
    let bgColor: string;
    let textColor: string;

    switch (order_status) {
      case 'unfulfilled':
        bgColor = '#FFD6D6';
        textColor = '#990000';
        break;
      case 'partially_fulfilled':
        bgColor = '#FFF4CC';
        textColor = '#996C00';
        break;
      case 'on_hold':
        bgColor = '#FFEBCC';
        textColor = '#996C00';
        break;
      case 'scheduled':
        bgColor = '#CCE5FF';
        textColor = '#004085';
        break;
      case 'picked':
        bgColor = '#E6F7FF';
        textColor = '#007BFF';
        break;
      case 'packed':
        bgColor = '#D4FAE6';
        textColor = '#529C7B';
        break;
      case 'shipped':
        bgColor = '#D9EDF7';
        textColor = '#31708F';
        break;
      case 'processing':
        bgColor = '#FFF3CD';
        textColor = '#856404';
        break;
      case 'in_transit':
        bgColor = '#CCE5FF';
        textColor = '#004085';
        break;
      case 'pending':
        bgColor = '#FFD6A5';
        textColor = '#855800';
        break;
      case 'started':
        bgColor = '#FFE6CC';
        textColor = '#996C00';
        break;
      case 'partial':
        bgColor = '#FFF3E6';
        textColor = '#995700';
        break;
      case 'full':
        bgColor = '#D4FAE6';
        textColor = '#529C7B';
        break;
      case 'canceled':
        bgColor = '#FBDDD8';
        textColor = '#970000';
        break;
      case 'returned':
        bgColor = '#FFD6D6';
        textColor = '#990000';
        break;
      case 'failed':
        bgColor = '#FFE6E6';
        textColor = '#FF0000';
        break;
      default:
        bgColor = '#E2E2E2';
        textColor = '#595959';
        break;
    }

    return {
      'background-color': bgColor,
      'color': textColor,
    };
  }

  getOrdereByStatus(orderations: IOrder[], order_status: string): number {
    return orderations.filter(ordere => ordere.order_status == order_status).length
  };

  sortBy(key: string, mode: 'asc' | 'desc') {
    if(key == this.activeSortBy && mode == this.activeSortMode) return;

    this.activeSortBy = key;
    this.activeSortMode = mode;
    this.toPage(this.page)
  }

  details(order: IOrder) {
    if (this.isPlaceholder(order)) return;
    let filter:any = {}
    if(this.key && this.key != ''){
      filter.keyword = this.key;
    }
    filter.filter = encodeFilterObject(this.unparsedFilter);
    let queryParams = { page: this.page.number, count: this.page.size, ...filter };
    this.router.navigate(['/orders/', order.id],{
      queryParams: queryParams,
    });
  }

  isPlaceholder(order: IOrder): boolean {
    return (
      order.ato_processing_status === AtoProcessingStatus.processing ||
      order.ato_processing_status === AtoProcessingStatus.needs_review
    );
  }

  elapsedFor(startIso?: string | null): string {
    if (!startIso) return '0s';
    const start = new Date(startIso).getTime();
    if (isNaN(start)) return '0s';
    const totalSeconds = Math.max(0, Math.floor((this.now - start) / 1000));
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    return minutes > 0 ? `${minutes}m ${seconds}s` : `${seconds}s`;
  }

  createManually(): void {
    this.router.navigate(['/quotations/add']);
  }

  deleteProcessingRow(order: IOrder): void {
    this.store.dispatch(quotationsActions.deleteQuotation({ id: order.id, confirm: true }));
    this.toPage(this.page);
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((sub => sub.unsubscribe()))
    this.destroy$.next();
    this.destroy$.complete();
  }
}
